import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, imgUrl } from "@/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const ProductForm = ({ open, onClose, product, categories, onSaved }) => {
  const [form, setForm] = useState({ title: "", description: "", category: "", image_url: "", featured: false });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(
        product
          ? { title: product.title, description: product.description, category: product.category, image_url: product.image_url, featured: product.featured }
          : { title: "", description: "", category: categories[0]?.slug || "", image_url: "", featured: false }
      );
      setFile(null);
    }
  }, [open, product, categories]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let imageUrl = form.image_url;
      if (file) {
        const fd = new FormData();
        fd.append("files", file);
        fd.append("category", form.category);
        fd.append("create_products", "false");
        const { data } = await api.post("/upload", fd);
        imageUrl = data[0].url;
      }
      if (!imageUrl) {
        toast.error("Lütfen bir görsel seçin");
        setSaving(false);
        return;
      }
      const payload = { ...form, image_url: imageUrl };
      if (product) {
        await api.put(`/products/${product.id}`, payload);
        toast.success("Ürün güncellendi");
      } else {
        await api.post("/products", payload);
        toast.success("Ürün eklendi");
      }
      onSaved();
    } catch {
      toast.error("Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-none" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{product ? "Ürünü Düzenle" : "Yeni Ürün"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4" data-testid="product-form">
          <div className="flex flex-col gap-2">
            <Label>Başlık</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="product-title-input" />
          </div>
          <div className="flex flex-col gap-2">
            <Label>Açıklama</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="product-description-input" />
          </div>
          <div className="flex flex-col gap-2">
            <Label>Kategori</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="product-category-select"><SelectValue placeholder="Kategori seç" /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.slug} data-testid={`product-category-${c.slug}`}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label>Görsel</Label>
            {form.image_url && !file && (
              <img src={imgUrl(form.image_url)} alt="Mevcut" className="h-24 w-24 object-cover bg-beige-soft" />
            )}
            <Input type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="product-image-input" />
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="product-featured-switch" />
            <Label>Öne çıkan ürün</Label>
          </div>
          <Button type="submit" disabled={saving} className="bg-stone-900 hover:bg-wood text-stone-50 rounded-none mt-2" data-testid="product-save-button">
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
